import VendNumber, { add, divide, round, subtract, vn } from 'vend-number'
import { trackError } from '@vend/utilities'
import {
  GraphQLFulfilmentStatus,
  GraphQLPicklist,
  GraphQLPickListType,
  GraphQLSale,
} from '../factories/gql-types'
import {
  ClientApiSaleData,
  ToClientApiSaleParams,
} from '../factories/sale-transformer'
import { CustomFieldMap, CustomFieldValue } from '../models/custom-fields'
import { FulfilmentType } from '../models/fulfilment'
import { LineItem, LineItemTaxComponent } from '../models/line-item'
import { GqlCustomField } from '../queries/sale_create'
import { ReduxCurrentSale } from '../state/slices/currentSale/currentSaleSlice'
import { WEB_REGISTER } from '../components/web-register.constant'
import { getPaymentsTotal, hasDiscount, isValidCustomer } from './sale'

export const formatCustomFieldForMutation = (
  customFieldValue: CustomFieldValue
): GqlCustomField => {
  switch (customFieldValue.type) {
    case 'string':
    case 'date':
    case 'product_id':
    case 'qrcode':
      return {
        name: customFieldValue.name,
        stringValue: customFieldValue.value as string,
      }
    case 'integer':
      return {
        name: customFieldValue.name,
        integerValue: customFieldValue.value?.toString(),
      }
    case 'boolean':
      return {
        name: customFieldValue.name,
        booleanValue: customFieldValue.value! as boolean,
      }
    case 'string_array':
      return {
        name: customFieldValue.name,
        stringArrayValue: customFieldValue.value! as string[],
      }
    default:
      return {
        name: customFieldValue.name,
        stringValue: customFieldValue.value as string,
      }
  }
}

export const entityCustomFieldsToMap = (
  sale: Readonly<ReduxCurrentSale>,
  lineItems: Readonly<LineItem[]>
): CustomFieldMap => {
  const lineItemsCustomFields = lineItems.reduce<CustomFieldMap>(
    (acc, lineItem) => {
      const customField = lineItem.customFields.map(customField => {
        return formatCustomFieldForMutation(customField)
      })
      return {
        ...acc,
        [lineItem.localId]: { values: customField, entity: 'LINE_ITEM' },
      }
    },
    {}
  )

  const saleCustomFields = sale.customFields.map(customField => {
    return formatCustomFieldForMutation(customField)
  })

  return {
    ...lineItemsCustomFields,
    [sale.localId]: { values: saleCustomFields, entity: 'SALE' },
  }
}

export const getItemFulfilmentMap = ({
  picklists,
  fulfillments,
}: Pick<GraphQLSale, 'fulfillments' | 'picklists'>) => {
  const itemFulfilmentMap = (picklists || []).reduce(
    (result, { type, lineItems, note }) => {
      for (const { saleLineItem } of lineItems) {
        if (!saleLineItem) {
          continue
        }

        switch (type) {
          case GraphQLPickListType.DISPATCH:
            result.set(saleLineItem.id, [FulfilmentType.DELIVERY, false, note])
            break
          case GraphQLPickListType.PICKUP:
            result.set(saleLineItem.id, [FulfilmentType.PICKUP, false, note])
            break
        }
      }
      return result
    },
    new Map<string, [FulfilmentType, boolean, string]>()
  )

  // Track which line items are still on a picklist (i.e. have an unfulfilled
  // remainder). A partially fulfilled line item appears in BOTH a picklist
  // (remaining qty) and a fulfillment (dispatched/picked-up qty). Such items
  // must not be marked as completed so they stay under their
  // delivery/pickup banner until they are fully fulfilled.
  const itemsWithUnfulfilledRemainder = new Set(itemFulfilmentMap.keys())

  return (fulfillments || []).reduce((result, { status, lineItems, note }) => {
    for (const { saleLineItem } of lineItems) {
      if (!saleLineItem) {
        continue
      }

      // Don't override a line item that still has an unfulfilled remainder on
      // a picklist; it is only partially fulfilled.
      if (itemsWithUnfulfilledRemainder.has(saleLineItem.id)) {
        continue
      }

      switch (status) {
        case GraphQLFulfilmentStatus.DISPATCHED:
          result.set(saleLineItem.id, [FulfilmentType.DELIVERY, true, note])
          break
        case GraphQLFulfilmentStatus.PICKED_UP:
          result.set(saleLineItem.id, [FulfilmentType.PICKUP, true, note])
          break
      }
    }
    return result
  }, itemFulfilmentMap)
}

export const getItemFulfilmentOutletMap = (
  picklists: readonly GraphQLPicklist[] | null | undefined
): Map<string, string> => {
  const itemFulfilmentOutletMap = new Map<string, string>()

  for (const { outlet, lineItems } of picklists || []) {
    if (!outlet?.id) {
      continue
    }

    for (const { saleLineItem } of lineItems || []) {
      if (saleLineItem?.id) {
        itemFulfilmentOutletMap.set(saleLineItem.id, outlet.id)
      }
    }
  }

  return itemFulfilmentOutletMap
}

const getLineItemProductId = (lineItem: LineItem) => {
  const productId = lineItem.product.id

  if (!productId) {
    trackError('[Sale Transformer] Failed to get product id from line item', {
      data: {
        stack: new Error().stack,
      },
    })
  }

  return productId
}

const getLineItemTags = (lineItem: LineItem) => {
  if (!lineItem?.product?.categories) {
    return []
  }

  return lineItem.product.categories.map(category => category.name)
}

export const formatToClientApi = (
  sale: Readonly<ToClientApiSaleParams>,
  lineItems: ReadonlyArray<Readonly<LineItem>>,
  discountProductId: string
): ClientApiSaleData => {
  const clientApiSale: ClientApiSaleData = {
    id: sale.id || sale.localId,

    client_id: WEB_REGISTER.APPLICATION_NAME,
    client_sale_id: sale.localId,

    status: sale.status,

    totals: {
      total_tax: sale.finals.tax,
      total_price: sale.finals.exclusivePrice,
      total_paid: round(
        subtract(getPaymentsTotal(sale.payments), sale.finals.tip),
        2
      ),

      // @deprecated
      total_to_pay: sale.finals.inclusivePrice,
    },

    line_items: [],
  }

  clientApiSale.line_items = lineItems.map(lineItem => {
    let taxRate: VendNumber | null = null

    if (lineItem.taxInfo && lineItem.taxInfo.id) {
      taxRate = lineItem.taxComponents.reduce<VendNumber>(
        (s, t: LineItemTaxComponent) => vn(add(vn(s), vn(t.rate.rate))),
        vn(0)
      )
    }

    const unitTax = vn(lineItem.quantity).isZero()
      ? 0
      : divide(vn(lineItem.totals.tax), vn(lineItem.quantity))

    return {
      id: lineItem.localId,
      product_id: getLineItemProductId(lineItem),
      tags: getLineItemTags(lineItem),
      gift_card_number: lineItem.giftCardNumber,
      name: lineItem.product.name,
      quantity: lineItem.quantity,
      sku: lineItem.product.sku,
      unit_price: round(lineItem.price, 5),
      unit_tax: round(unitTax, 5),
      tax_id:
        lineItem.taxInfo && lineItem.taxInfo.id ? lineItem.taxInfo.id : null,
      tax_rate: taxRate,
      image_url: lineItem.product.image_url,
      discount: round(lineItem.displayDiscount, 5),
    }
  })

  if (isValidCustomer(sale.customer)) {
    clientApiSale.customer_id = sale.customer.id
  }

  if (hasDiscount(sale.discount)) {
    clientApiSale.discount = {
      product_id: discountProductId,
      price: round(sale.discount.price, 5),
      tax: round(sale.discount.tax, 5),
      tax_id: sale.discount.taxInfo!.id,
    }
  }

  return clientApiSale
}
